import React, { Fragment, useEffect, useMemo, useState } from 'react';

import classNames from 'classnames';
import moment from 'moment';

import ActualVsBudgetReportDetailedTableRow from '../../actual_vs_budget_report/ActualVsBudgetReportDetailedTableRow';
import ActualVsBudgetReportHeader from '../../actual_vs_budget_report/ActualVsBudgetReportHeader';
import ActualVsBudgetReportLegend from '../../actual_vs_budget_report/ActualVsBudgetReportLegend';
import ActualVsBudgetReportTransactionsModal from '../../actual_vs_budget_report/ActualVsBudgetReportTransactionsModal';
import { buildGroupedHeaderRows } from '../../actual_vs_budget_report/helpers/reportHeaderGroups';
import { GROUP_START_CELL, reportNegativeColourClass } from '../../actual_vs_budget_report/helpers/reportRowStyles';
import { varianceColourClass } from '../../actual_vs_budget_report/helpers/varianceColour';
import { ReportColumnHeaderRow, ReportGroupHeaderRow } from '../../actual_vs_budget_report/ReportTableHeaderRows';
import { ROW_BUDGET_LINE, ROW_COST_CENTRE, ROW_PROJECT, ROW_TOTAL } from '../../actual_vs_budget_report/types/ReportRowType';
import { NOT_IN_CURRENT_STRUCTURE_ID } from '../../actual_vs_budget_report/types/ReportType';

import { formatDateWithTz } from 'Helpers/dateUtils';
import { formatFigure, formatNegativeMoney } from 'Helpers/formatService';
import { generateKey } from 'Helpers/misc';
import { modalOptions } from 'Helpers/modalOptions';

import CheckboxInput from 'CommonComponents/CheckboxInput';
import TableFilterModal from 'CommonComponents/TableFilterModal';

import useModal from 'Hooks/useModal';

const MODULE_DEPARTMENT = 'department';

const ArchivedDepartmentDetailedTable = ({ details, expanded, module, onToggleExpansion, onToggleAllExpansion, storedReport = null }) => {
    const [displayDates, setDisplayDates] = useState({});
    const [currentTransactions, setCurrentTransactions] = useState([]);

    const transactionModal = useModal({ options: { ...modalOptions(true), backdrop: 'dynamic' } });

    // A department / cost-centre report totals expenditure only, so an underspend
    // is always the favourable direction here.
    const footerVariance = (value, groupStart = false) => (
        <td className={classNames('inbud-numeric-cell', varianceColourClass(value, false, true), { [GROUP_START_CELL]: groupStart })}>
            {formatFigure(value, formatNegativeMoney)}
        </td>
    );

    // Grand-total footer cell — dark fill, so negatives take the lighter red tint.
    const footerFigure = (value, groupStart = false) => (
        <td className={classNames('inbud-numeric-cell', reportNegativeColourClass(value, ROW_TOTAL), { [GROUP_START_CELL]: groupStart })}>
            {formatFigure(value, formatNegativeMoney)}
        </td>
    );

    useEffect(() => {
        toggleDisplayDates();
    }, [details]);

    // A comparison version's unmatched lines are gathered under a synthetic
    // sentinel department. This page reports on one real department, so exclude it
    // explicitly rather than relying on it being appended last.
    const realDepartmentKeys = (data) => Object.keys(data?.expense?.departments || {}).filter((name) => name !== NOT_IN_CURRENT_STRUCTURE_ID);

    const departmentKey = useMemo(() => {
        return realDepartmentKeys(details?.data)[0];
    }, [details]);

    const departmentData = useMemo(() => {
        const key = realDepartmentKeys(details?.data)[0];

        return details?.data?.expense?.departments?.[key] || null;
    }, [details]);

    const costCenterKey = useMemo(() => {
        return Object.keys(departmentData?.costcenters || {})[0];
    }, [departmentData]);

    const costcenterData = useMemo(() => {
        return departmentData?.costcenters?.[costCenterKey] || null;
    }, [departmentData, costCenterKey]);

    const data = useMemo(() => {
        return module === MODULE_DEPARTMENT ? departmentData : costcenterData;
    }, [costcenterData, departmentData, module]);

    // Same grouped header as the live report. An archived report is rendered
    // outside the report provider, so no supplementary columns are visible — the
    // header derives its spans from that same empty set, keeping it aligned with
    // the cells the shared row component renders.
    const headerRows = useMemo(() => {
        if (!details?.headings) {
            return { groups: [], columns: [] };
        }

        return buildGroupedHeaderRows({
            supplementaryKeys: [],
            visibleColumns: {},
            months: details.datesArray || [],
            rowLabel: $t('Cost Centre / Project / Budget Line'),
        });
    }, [details, displayDates]);

    const dates = useMemo(() => {
        return (details?.datesArray || []).reduce((result, dateString) => {
            const date = moment(dateString, 'MM-YYYY');
            const year = date.year();

            if (!result[year]) {
                result[year] = { months: [] };
            }

            result[year].months.push({ label: date.format('MMM'), value: dateString });

            return result;
        }, {});
    }, [details]);

    const toggleDisplayDates = (value = true) => {
        const result = (details?.datesArray || []).reduce((result, dateString) => {
            result[dateString] = value;
            return result;
        }, {});

        setDisplayDates(result);
    };

    const toggleDisplayDate = (key) => {
        setDisplayDates((dates) => ({ ...dates, [key]: !dates[key] }));
    };

    const dataClickHandle = (data, month) => {
        if (month && data?.length) {
            data = filterTransactionsByMonth(data, month);
        }

        setCurrentTransactions(data || []);
        transactionModal.show();
    };

    const filterTransactionsByMonth = (data, month) => {
        const formattedMonth = formatDateWithTz(month, 'MM-YYYY', 'YYYY-MM');
        return data.filter((item) => item.date.includes(formattedMonth));
    };

    return (
        <div>
            {/* Department drill-downs render no comparison columns, so no "Compare to" chip. */}
            <ActualVsBudgetReportHeader showComparisonChip={false} storedReport={storedReport} />
            <div className="tw-flex tw-gap-x-4 tw-justify-between tw-items-end tw-mb-4">
                <div className="tw-py-1 tw-flex tw-gap-x-2 tw-items-baseline">
                    <div className="tw-text-xs tw-font-medium tw-text-charcoal-50">{$t('Row view')}</div>
                    <div className="tw-flex tw-gap-x-1.5">
                        <button className="tw-text-xs tw-font-medium tw-text-charcoal tw-underline" onClick={() => onToggleAllExpansion()}>
                            {$t('Expand all')}
                        </button>
                        <button className="tw-text-xs tw-font-medium tw-text-charcoal tw-underline" onClick={() => onToggleAllExpansion(false)}>
                            {$t('Collapse all')}
                        </button>
                    </div>
                </div>
                <div className="tw-flex tw-items-center tw-gap-x-2">
                    <TableFilterModal>
                        <div className="tw-flex tw-justify-between tw-items-center tw-mb-2">
                            <div className="tw-text-xs tw-text-charcoal">{$t('Display Months')}</div>
                            <div className="tw-space-x-2">
                                <button className="tw-text-xs tw-font-semibold tw-underline" onClick={(e) => toggleDisplayDates()}>
                                    {$t('Select all')}
                                </button>
                                <button className="tw-text-xs tw-font-semibold tw-underline" onClick={(e) => toggleDisplayDates(false)}>
                                    {$t('Unselect all')}
                                </button>
                            </div>
                        </div>
                        <div className="tw-space-y-3">
                            {Object.entries(dates).map(([year, yearData]) => (
                                <div key={year} className="tw-space-y-1">
                                    <div className="tw-text-xs tw-font-medium">{year}</div>
                                    <div className="tw-grid tw-grid-cols-6 tw-gap-x-3 tw-gap-y-1">
                                        {yearData.months.map((month) => (
                                            <CheckboxInput
                                                key={month.value}
                                                id={month.value}
                                                label={month.label}
                                                checked={displayDates[month.value] || false}
                                                onChange={() => toggleDisplayDate(month.value)}
                                            />
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </TableFilterModal>
                </div>
            </div>
            {departmentData && (
                <div className="inbud-table_wrapper avb-report-wrapper">
                    {/* `avb-report-table` is what tables/_avb-report.scss keys on —
                        without it this table falls back to the shared dense grid in
                        tables/_style.scss (grey striping, 2px rules). */}
                    <table className="inbud-table avb-report-table inbud-table_first-sticky">
                        <thead className="tw-sticky tw-top-0 tw-z-10">
                            <ReportGroupHeaderRow groups={headerRows.groups} />
                            <ReportColumnHeaderRow columns={headerRows.columns} />
                        </thead>
                        <tbody>
                            {Object.entries(departmentData.costcenters).map(([costcenterName, costcenterData]) => {
                                const workforceCCData = costcenterData.workforce ?? null;
                                const projectsCcData = costcenterData.projects && _.isObject(costcenterData.projects) ? costcenterData.projects : null;

                                return (
                                    <Fragment key={costcenterName}>
                                        {module === MODULE_DEPARTMENT && (
                                            <ActualVsBudgetReportDetailedTableRow
                                                onDataClick={(month) => dataClickHandle(costcenterData.source, month)}
                                                key={generateKey()}
                                                rowType={ROW_COST_CENTRE}
                                                favourableWhenNegative={false}
                                                data={costcenterData.total}
                                                datesArray={details.datesArray}
                                                displayDates={displayDates}
                                                show={true}
                                                toggleHandler={() => onToggleExpansion('expense', departmentKey, costcenterName)}
                                                expandable={true}
                                                expanded={expanded.expense[departmentKey].costcenters[costcenterName].expanded}
                                                actualData={costcenterData.source || []}
                                            />
                                        )}

                                        {Object.keys(costcenterData.codes.data).map((code) => {
                                            return (
                                                <ActualVsBudgetReportDetailedTableRow
                                                    onDataClick={(month) => dataClickHandle(costcenterData.codes.source[code], month)}
                                                    key={generateKey()}
                                                    rowType={ROW_BUDGET_LINE}
                                                    favourableWhenNegative={false}
                                                    data={costcenterData.codes.data[code]}
                                                    datesArray={details.datesArray}
                                                    displayDates={displayDates}
                                                    level={module === MODULE_DEPARTMENT ? 2 : 1}
                                                    show={
                                                        module === MODULE_DEPARTMENT
                                                            ? expanded.expense[departmentKey].costcenters[costcenterName].expanded
                                                            : true
                                                    }
                                                    toggleHandler={() => onToggleExpansion('expense', departmentKey, costcenterName, code)}
                                                    expandable={false}
                                                    expanded={expanded.expense[departmentKey].costcenters[costcenterName].codes[code].expanded}
                                                    actualData={costcenterData.codes.source[code] || []}
                                                    tooltipId={generateKey()}
                                                    isInternalRow={true}
                                                />
                                            );
                                        })}

                                        {!!projectsCcData && (
                                            <>
                                                {Object.keys(projectsCcData).map((ccProject) => {
                                                    const ccProjectData = projectsCcData[ccProject];

                                                    return (
                                                        <Fragment key={ccProject}>
                                                            <ActualVsBudgetReportDetailedTableRow
                                                                onDataClick={(month) => dataClickHandle(ccProjectData.source, month)}
                                                                key={generateKey()}
                                                                rowType={ROW_PROJECT}
                                                                favourableWhenNegative={false}
                                                                data={ccProjectData.total}
                                                                datesArray={details.datesArray}
                                                                displayDates={displayDates}
                                                                level={module === MODULE_DEPARTMENT ? 2 : 1}
                                                                show={
                                                                    module === MODULE_DEPARTMENT
                                                                        ? expanded.expense[departmentKey].costcenters[costcenterName].expanded
                                                                        : true
                                                                }
                                                                toggleHandler={() =>
                                                                    onToggleExpansion('expense', departmentKey, costcenterName, null, null, ccProject)
                                                                }
                                                                expandable={true}
                                                                expanded={
                                                                    expanded.expense[departmentKey].costcenters[costcenterName].projects[ccProject].expanded
                                                                }
                                                                actualData={ccProjectData.source || []}
                                                                tooltipId={generateKey()}
                                                            />
                                                            {Object.keys(ccProjectData.codes.data).map((code) => {
                                                                return (
                                                                    <ActualVsBudgetReportDetailedTableRow
                                                                        onDataClick={(month) => dataClickHandle(ccProjectData.codes.source[code], month)}
                                                                        key={generateKey()}
                                                                        rowType={ROW_BUDGET_LINE}
                                                                        favourableWhenNegative={false}
                                                                        data={ccProjectData.codes.data[code]}
                                                                        datesArray={details.datesArray}
                                                                        displayDates={displayDates}
                                                                        level={3}
                                                                        show={
                                                                            module === MODULE_DEPARTMENT
                                                                                ? expanded.expense[departmentKey].costcenters[costcenterName].expanded &&
                                                                                  expanded.expense[departmentKey].costcenters[costcenterName].projects[
                                                                                      ccProject
                                                                                  ].expanded
                                                                                : expanded.expense[departmentKey].costcenters[costcenterName].projects[
                                                                                      ccProject
                                                                                  ].expanded
                                                                        }
                                                                        toggleHandler={() =>
                                                                            onToggleExpansion('expense', departmentKey, costcenterName, code, null, ccProject)
                                                                        }
                                                                        expandable={false}
                                                                        expanded={true}
                                                                        actualData={ccProjectData.codes.source[code] || []}
                                                                        tooltipId={generateKey()}
                                                                        isInternalRow={true}
                                                                    />
                                                                );
                                                            })}
                                                        </Fragment>
                                                    );
                                                })}
                                            </>
                                        )}

                                        {!!workforceCCData && (
                                            <>
                                                <ActualVsBudgetReportDetailedTableRow
                                                    onDataClick={(month) => dataClickHandle(workforceCCData.source, month)}
                                                    key={generateKey()}
                                                    rowType={ROW_PROJECT}
                                                    favourableWhenNegative={false}
                                                    data={workforceCCData.total}
                                                    datesArray={details.datesArray}
                                                    displayDates={displayDates}
                                                    level={module === MODULE_DEPARTMENT ? 2 : 1}
                                                    show={
                                                        module === MODULE_DEPARTMENT
                                                            ? expanded.expense[departmentKey].costcenters[costcenterName].expanded
                                                            : true
                                                    }
                                                    toggleHandler={() => onToggleExpansion('expense', departmentKey, costcenterName, null, true)}
                                                    expandable={true}
                                                    expanded={!!expanded?.expense[departmentKey]?.costcenters[costcenterName]?.workforce?.expanded}
                                                    actualData={workforceCCData.source || []}
                                                    tooltipId={generateKey()}
                                                    isInternalRow={true}
                                                />

                                                {Object.keys(workforceCCData.codes && workforceCCData.codes.data ? workforceCCData.codes.data : {}).map(
                                                    (code) => {
                                                        return (
                                                            <ActualVsBudgetReportDetailedTableRow
                                                                onDataClick={(month) => dataClickHandle(workforceCCData.codes.source[code], month)}
                                                                key={generateKey()}
                                                                rowType={ROW_BUDGET_LINE}
                                                                favourableWhenNegative={false}
                                                                data={workforceCCData.codes.data[code]}
                                                                datesArray={details.datesArray}
                                                                displayDates={displayDates}
                                                                level={module === MODULE_DEPARTMENT ? 3 : 2}
                                                                show={
                                                                    module === MODULE_DEPARTMENT
                                                                        ? expanded.expense[departmentKey].costcenters[costcenterName].expanded &&
                                                                          expanded.expense[departmentKey].costcenters[costcenterName].workforce.expanded
                                                                        : expanded.expense[departmentKey].costcenters[costcenterName].workforce.expanded
                                                                }
                                                                toggleHandler={() => onToggleExpansion('expense', departmentKey, costcenterName, code)}
                                                                expandable={false}
                                                                expanded={false}
                                                                actualData={workforceCCData.codes.source[code] || []}
                                                                tooltipId={generateKey()}
                                                                isInternalRow={true}
                                                            />
                                                        );
                                                    },
                                                )}
                                            </>
                                        )}
                                    </Fragment>
                                );
                            })}
                        </tbody>
                        <tfoot className="tw-text-xs [&_td]:!tw-font-normal [&_td]:!tw-bg-dark-blue">
                            {'total' in data && (
                                <tr>
                                    <td>{$t('Total')}</td>
                                    {/* An archived report renders outside the report provider, so monthly
                                        phasing is off: neither the header, the rows, nor this footer emit
                                        per-month columns. */}
                                    {footerFigure(data.total.month_budget_total, true)}
                                    {footerFigure(data.total.month_actual_total)}
                                    {footerVariance(data.total.month_variance)}

                                    {footerFigure(data.total.period_budget_total, true)}
                                    {footerFigure(data.total.period_actual_total)}
                                    {footerVariance(data.total.period_variance)}

                                    {footerFigure(data.total.budget_total, true)}
                                    {footerFigure(data.total.actual_total)}
                                    {footerVariance(data.total.total_variance)}
                                </tr>
                            )}
                        </tfoot>
                    </table>
                </div>
            )}
            {/* No comparison columns on this table, so the LE/AB sentence drops. */}
            <ActualVsBudgetReportLegend versionName={storedReport?.budget_name || ''} comparisonName="" />
            <ActualVsBudgetReportTransactionsModal modal={transactionModal} transactions={currentTransactions} />
        </div>
    );
};

export default ArchivedDepartmentDetailedTable;
